import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { Search, Plus, Download, Edit2, Trash2, MessageSquare, Users } from "lucide-react";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import CreateGroupModal from "../../components/CreateGroupModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import ContactAvatar from "../../components/ContactAvatar";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    contacts.forEach((contact) => {
      const idx = state.findIndex((c) => c.id === contact.id);
      if (idx !== -1) state[idx] = contact;
      else newContacts.push(contact);
    });
    return [...state, ...newContacts];
  }
  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const idx = state.findIndex((c) => c.id === contact.id);
    if (idx !== -1) { state[idx] = contact; return [...state]; }
    return [contact, ...state];
  }
  if (action.type === "DELETE_CONTACT") {
    return state.filter((c) => c.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const Contacts = () => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
        setHasMore(data.hasMore);
      } catch (err) { toastError(err); }
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    const handleContact = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      if (data.action === "delete")
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
    };

    socket.on("contact", handleContact);

    return () => {
      socket.off("contact", handleContact);
    };
  }, []);

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", { contactId, userId: user?.id, status: "open" });
      history.push(`/tickets/${ticket.id}`);
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      history.go(0);
    } catch (err) { toastError(err); }
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  return (
    <MainContainer>
      <ContactModal
        open={contactModalOpen}
        onClose={() => { setSelectedContactId(null); setContactModalOpen(false); }}
        contactId={selectedContactId}
      />
      <CreateGroupModal
        open={createGroupModalOpen}
        onClose={() => setCreateGroupModalOpen(false)}
      />
      <ConfirmationModal
        title={deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
          : i18n.t("contacts.confirmationModal.importTitlte")}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => deletingContact ? handleDeleteContact(deletingContact.id) : handleimportContact()}
      >
        {deletingContact
          ? i18n.t("contacts.confirmationModal.deleteMessage")
          : i18n.t("contacts.confirmationModal.importMessage")}
      </ConfirmationModal>

      <MainHeader>
        <Title>{i18n.t("contacts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              placeholder={i18n.t("contacts.searchPlaceholder")}
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
              className="pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full sm:w-52 transition-all"
            />
          </div>
          <button
            onClick={() => setConfirmOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <Download className="w-4 h-4" />
            {i18n.t("contacts.buttons.import")}
          </button>
          <button
            onClick={() => setCreateGroupModalOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <Users className="w-4 h-4" />
            Crear Grupo
          </button>
          <button
            onClick={() => { setSelectedContactId(null); setContactModalOpen(true); }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            {i18n.t("contacts.buttons.add")}
          </button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6" onScroll={handleScroll}>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                <th className="w-12 px-4 py-3 text-left" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                  {i18n.t("contacts.table.name")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                  {i18n.t("contacts.table.whatsapp")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                  {i18n.t("contacts.table.email")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                  {i18n.t("contacts.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {contacts.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <p className="text-base font-semibold text-slate-500 dark:text-slate-400">No hay contactos</p>
                    <p className="text-sm text-slate-400 mt-1">Agrega un contacto o importa desde WhatsApp</p>
                  </td>
                </tr>
              ) : (
                contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <ContactAvatar
                        src={contact.profilePicUrl}
                        alt={contact.name}
                        className="w-9 h-9 rounded-full object-cover border border-slate-200 dark:border-slate-700"
                        size="sm"
                        contactId={contact.id}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-medium text-slate-900 dark:text-slate-100">{contact.name}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-500 dark:text-slate-400 whitespace-nowrap">{contact.number}</td>
                    <td className="px-4 py-3 text-center text-slate-500 dark:text-slate-400 whitespace-nowrap">{contact.email || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleSaveTicket(contact.id)}
                          title="Iniciar conversación"
                          className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setSelectedContactId(contact.id); setContactModalOpen(true); }}
                          title="Editar"
                          className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <Can
                          role={user.profile}
                          perform="contacts-page:deleteContact"
                          permissions={user.permissions}
                          yes={() => (
                            <button
                              onClick={() => { setConfirmOpen(true); setDeletingContact(contact); }}
                              title="Eliminar"
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {loading && (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-slate-400 text-sm">
                    <div className="flex justify-center"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainContainer>
  );
};

export default Contacts;
